import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ProductService } from '../../data-access/product.service';
import { Subject, map, takeUntil } from 'rxjs';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Product } from '../../interfaces/product.interface';
import { ProductCategory } from '../../interfaces/productCategory.interface';
import { ProductCategoryService } from '../../data-access/productCategory.service';
import { Plan } from '../../interfaces/plan.interface';
import { PlanService } from '../../data-access/plan.service';
import { GlCodeService } from '../../data-access/gl-code.service';
import { GLCode } from '../../interfaces/glCode.interface';

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule
  ],
  providers: [
    ProductService,
    ProductCategoryService,
    PlanService,
    GlCodeService
  ],
  selector: 'equine-ui-nx-product-admin',
  templateUrl: './product-admin.component.html',
  styleUrls: ['./product-admin.component.css']
})
export class ProductAdminComponent implements OnInit, OnDestroy {

  #productService = inject(ProductService);
  #productCategoryService = inject(ProductCategoryService);
  #planService = inject(PlanService);
  #glCodeService = inject(GlCodeService);
  unsubscribe$ = new Subject<void>();

  id = '';
  form: FormGroup;
  products: Product[] = [];
  productCategories: ProductCategory[] = [];
  plans: Plan[] = [];
  glCodes: (GLCode & {composedLabel: string})[] = [];
  showProducts = false;

  constructor() {
    this.form = new FormGroup({
      nickname: new FormControl('', Validators.required),
      description: new FormControl(''),
      active: new FormControl(true, Validators.required),
      currency: new FormControl('usd', Validators.required),
      amount: new FormControl('', [Validators.required, Validators.min(1)]),
      productCategoryId: new FormControl('', Validators.required),
      planId: new FormControl('', Validators.required),
      glCodeId: new FormControl('', Validators.required)
    });
  }

  createNew() {
    if (this.form.valid) {
      this.#productService.create(this.form.value)
        .pipe(
          takeUntil(this.unsubscribe$)
        )
        .subscribe({
          next: (response) => {
            this.id = response.id;
            if (this.showProducts) {
              this.fetchProducts();
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.fetchPlans();
    this.fetchProductCategories();
    this.fetchProducts();
    this.fetchGLCodes();
  }

  fetchPlans() {
    this.#planService.getAll()
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe({
      next: (plans) => this.plans = plans
    });
  }

  fetchProductCategories() {
    this.#productCategoryService.getAll()
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe({
      next: (productCategories) => this.productCategories = productCategories
    });
  }

  fetchProducts() {
    this.#productService.getAll()
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe({
      next: (products) => this.products = products
    });
  }

  fetchGLCodes() {
    this.#glCodeService.getAll()
      .pipe(
        takeUntil(this.unsubscribe$),
        map((glCodes) => glCodes.map(each => ({...each, composedLabel: `${each.label} - [${each.glCode}]`})))
      )
      .subscribe({
        next: (glCodes) => {
          this.glCodes = glCodes;
        }
      });
  }

  toggleShowProducts() {
    this.showProducts = !this.showProducts;
    if (this.showProducts) {
      this.fetchProducts();
    }
  }

  editProduct(product: Product) {
    product.isEditing = true;
  }

  cancelEditing(product: Product) {
    product.isEditing = false;
  }

  updateProduct(product: Product) {
    if (!product.isEditing) return;
    const updatedProduct = {
      nickname: product.nickname,
      description: product.description,
      active: product.active,
      currency: product.currency,
      amount: product.amount,
      productCategoryId: product.productCategoryId,
      planId: product.planId,
      glCodeId: product.glCodeId
    };
    this.#productService.update(product.id, updatedProduct)
      .subscribe({
        next: () => {
          product.isEditing = false;
          this.fetchProducts();
        }
      });
  }

  deleteProduct(id: string) {
    this.#productService.delete(id).subscribe(() => {
      this.fetchProducts();
    });
  }
}
